<template>
  <div class="layout-base" ref="root">
    <div
      v-if="isStg"
      style="clip-path: polygon(0 0, 100% 0, 0 100%)"
      class="layout-base-stg-label"
    >
      STG
    </div>
    <template v-if="!isWebParts">
      <BaseLink
          v-if="skipLinkTarget"
          id="layouts-global-skip-link"
          :to="skipLinkTarget"
          class="u-screen-reader-only"
      >
        {{ ls(skipLinkMessage) }}
      </BaseLink>
      <slot name="header" />
    </template>

    <div
      :class="{
        'layout-base-body': $slots.header,
        'webparts-padding': isWebParts
      }"
    >
      <template v-if="!isWebParts">
        <slot v-if="$slots.breadcrumbs" name="breadcrumbs" />
      </template>
      <main id="main" :class="{'webparts-padding': isWebParts}">
        <slot />
      </main>

      <template v-if="!isWebParts">
        <!-- 上に戻る -->
        <transition v-if="!isFixed" name="fadezoom">
          <button
            class="jps-frame-totop is-hidden-mobile"
            v-if="showToTop"
            @click="toTop()"
            aria-hidden="true"
          >
            <BaseIcon name="mdi/arrow-up" />
          </button>
        </transition>
      </template>
    </div>
    <template v-if="!isWebParts">
      <div v-if="$slots.footer" class="layout-base-footer">
        <slot name="footer" />
      </div>
      <div
        id="layer-scroll-back"
        class="layout-base-scroll-back"
      >
        <slot name="scroll-back" />
      </div>
    </template>
    <div id="layer-modal" />
    <div id="layer-tooltip" />
    <template v-if="!isWebParts">
      <div id="layer-float" class="layout-base-float" />
      <ClientOnly>
        <GlobalSnackbar />
      </ClientOnly>
      <div id="layer-loading" class="layout-base-loading">
        <slot name="loading" />
      </div>
      <div id="layer-notification" class="layout-base-notification">
        <slot name="notification" />
      </div>
      <ClientOnly>
        <GdprModal />
        <QuickLinks />
      </ClientOnly>
    </template>
  </div>
</template>

<script lang="ts" setup>
const { isWebParts } = useWebParts()
const route = useRoute()
const config = useRuntimeConfig()
const { ls } = useI18n()

const { skipLinkTarget, skipLinkMessage } = useSkipLink()

const isStg = computed(() => config.public.env === 'staging')

// パスが変わった時に、スキップリンクにフォーカスを移す
watch(
  () => route.path,
  () => {
    nextTick(() => {
      document.getElementById('layouts-global-skip-link')?.focus()
    })
  },
)

let smoothScroll = null

if (import.meta.client) {
  window.addEventListener(
    'scroll',
    () => {
      showToTop.value = window.scrollY > 600
    },
    { passive: true },
  )
  onMounted(() => {
    setTimeout(() => {
      const scrollBarWidth = getScrollbarSize()
      document.body.style.setProperty(
        '--scroll-bar-width',
        `${scrollBarWidth}px`,
      ) // bodyに設定しないとbodyから変数にアクセスできない
    }, 1000)
  })

  smoothScroll = inject('smoothScroll') as (payload?: any) => void
}

const isFixed = computed<boolean>(() => {
  if (route.meta?.fixed) {
    return route.meta?.fixed
  } else {
    return false
  }
})
const showToTop: Ref<boolean> = ref(false)

const root: Ref<HTMLElement> = ref()
const toTop = () => {
  if (smoothScroll) {
    smoothScroll({
      scrollTo: root.value,
      updateHistory: false,
      duration: 500,
    })
  }
}
</script>

<style lang="scss">
/* https://benfrain.com/preventing-body-scroll-for-modals-in-ios/ */
body:has(.base-modal,.is-full) {
  touch-action: none;
  -webkit-overflow-scrolling: none;
  overflow: hidden;
  overscroll-behavior: none;
  padding-right: var(--scroll-bar-width);
}
</style>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.layout-base-body {
  padding-top: $header-height-mobile;

  @include is-tablet() {
    padding-top: $header-height-tablet;
  }
}
.layout-base {
    position: relative;
    height: 100vh;
}
.layout-base-stg-label {
    pointer-events: none;
    position: fixed;
    left: 0px;
    top: 0px;
    z-index: 999999;
    height: 2rem;
    width: 2rem;
    background-color: $jps-key-red;
    padding-left: 0.25rem;
    font-size: 10px;
    color: $white;
}
.layout-base-footer {
    &:empty { display: none; }
}
.layout-base-scroll-back {
    position: fixed;
    left: 0px;
    top: 0px;
    z-index: 900;
    width: 100%;
    &:empty { display: none; }
}
#layer-modal {
  position: fixed;
  left: 0px;
  top: 0px;
  /* トップページのCurationModal用にjps-headerの2000より大きな値にする */
  z-index: 2001;
  width: 100%;
}
.layout-base-float {
    position: fixed;
    left: 0px;
    top: 0px;
    z-index: 1100;
    width: 100%;
}
.layout-base-loading {
    position: fixed;
    left: 0px;
    top: 0px;
    z-index: 99999;
    width: 100%;
}
.layout-base-notification {
    position: fixed;
    left: 0px;
    top: 0px;
    z-index: 9999;
    width: 100%;
}

.jps-frame-totop {
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  z-index: 790;
  cursor: pointer;
  @include no-user-select;
  appearance: none;
  -webkit-tap-highlight-color: transparent;

  border: none;
  color: white;
  background-color: $jps-key-red;
  border-radius: 15%;
  width: 60px;
  height: 60px;
  margin: auto;
  transition: all 0.2s;
  &:hover {
    background-color: $jps-key-red-hover;
    border-radius: 50%;
    transition: all 0.2s;
  }
  .jps-icon{
    margin-right: 0;
  }
}

main {
  min-height: calc(100vh - 3.5rem - 220px);
  @include is-mobile {
    min-height: calc(100vh - 3rem - 345px);
  }
}

.webparts-padding {
  padding-top: 0px !important;
  padding-bottom: 0px !important;
  overflow-x: hidden;
}
</style>
